import debounce from 'lodash.debounce';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const DISMISS_TYPE = {
    AUTO_DISMISS: 'auto_dismiss',
    MANUAL_DISMISS: 'manual_dismiss',
    COMMENTS_ACTIVE_DISMISS: 'comments_active_dismiss',
    PAGE_CLOSE_DISMISS: 'page_close_dismiss',
}; // these should be in sync with the values in BrandAlertViewed.Dismiss class

const STATE = {
    START: 'start',
    HALF_EXPANDED: 'half-expanded',
    FULL_EXPANDED: 'full-expanded',
    CLOSED: 'closed',
};

const getScrollPosition = (element) => ({
    scrollHeight: element.scrollHeight,
    scrollTop: element.scrollTop,
    clientHeight: element.clientHeight,
});

class HoverTracker {
    constructor(element, callback) {
        this._element = element;
        this._callback = callback;
        this._reset();
        this._track();
    }

    _reset() {
        this._timeEnter = null;
        this._maxY = null;
    }

    _track() {
        this._element.addEventListener('mouseenter', () => {
            this._timeEnter = Date.now();
        });
        this._element.addEventListener('mousemove', (e) => {
            this._maxY = Math.max(
                this._maxY,
                e.clientY + this._element.scrollTop - this._element.getBoundingClientRect().top,
            );
        });
        this._element.addEventListener('mouseleave', () => this.finalize());
    }

    finalize() {
        if (this._timeEnter) {
            const data = { duration: Date.now() - this._timeEnter, maxY: this._maxY };
            this._callback(data);
            this._reset();
        }
    }
}

class BrandAlertPopup {
    constructor(popupElement, data, client, popupNum) {
        this.client = client;
        this.popupNum = popupNum;
        this._dismissTimeSeconds = null;
        this._hoverTrackers = [];
        this._showNextTimeout = null;
        this._shownTime = null;

        this._popupState = STATE.START;
        this._popupContainer = document.querySelector('.js_popupContainer');
        this._popup = popupElement;
        this._popupHeader = this._popup.querySelector('.js_popupHeader');
        this._popupHeaderLinks = this._popup.querySelector('.js_headerLinks');
        this._popupHandleBar = this._popup.querySelector('.js_popupHandleBar'); // can be non-present on iOS
        this._popupInnerWrap = this._popup.querySelector('.js_innerWrap');
        this._popupContent = this._popup.querySelector('.js_content');
        this._secondaryWrap = this._popup.querySelector('.js_secondaryWrap');

        this._info = this._popup.querySelector('.js_info');
        this._infoHeader = this._info.querySelector('.js_infoHeader');
        this._infoTitle = this._info.querySelector('.js_isiTitle');
        this._popupInfoContent = this._popup.querySelector('.js_infoContent');

        this._indications = this._popup.querySelector('.js_indications');
        this._indicationsHeader = this._indications.querySelector('.js_indicationsHeader');
        this._indicationsTitle = this._indications.querySelector('.js_indicationsTitle');
        this._indicationsContent = this._indications.querySelector('.js_indicationsContent');

        this._populateData(data);
        this._setUpEvents();
        this.trackEvent({ eventName: 'alert_loaded' });
    }

    _populateData(data) {
        if (data.double_isi) {
            this._popup.classList.add('brand_alert_popup--double_isi');
        } else {
            this._popup.classList.remove('brand_alert_popup--double_isi');
        }

        if (data.wysiwyg_editor) {
            this._popup.classList.add('brand_alert_popup--wysiwyg_editor');
        } else {
            this._popup.classList.remove('brand_alert_popup--wysiwyg_editor');
        }

        this._popup.querySelector('.js_headerLinks').innerHTML = data.header;
        // Prepend because there's a dismiss button
        this._popup.querySelector('.js_headerContentWrap').innerHTML = data.headline;
        this._popupContent.innerHTML = data.expanded;
        this._indicationsContent.innerHTML = data.indications;
        this._popupInfoContent.innerHTML = data.isi;
        this._indicationsTitle.innerHTML = data.indications_title;
        this._infoTitle.innerHTML = data.isi_title;
        this._dismissTimeSeconds = data.dismiss_time_seconds;
        this._brandAlertViewedId = data.brand_alert_viewed_id;
    }

    _setUpEvents() {
        this._hoverTrackers = [
            // Expanded hover
            new HoverTracker(this._popupInnerWrap, (data) => {
                if (this._shownTime) this.trackEvent({ eventName: 'expanded_hover', data });
            }),
            // ISI hover
            new HoverTracker(this._popupInfoContent, (data) => this.trackEvent({ eventName: 'isi_hover', data })),
            // Indications hover
            new HoverTracker(this._indications, (data) => this.trackEvent({ eventName: 'indications_hover', data })),
        ];

        // block page scrolling when touching the indications header
        this._popupHandleBar?.addEventListener('touchmove', (e) => e.preventDefault());

        // block page scrolling when touching the indications header
        this._indicationsHeader.addEventListener('touchmove', (e) => e.preventDefault());

        // block page scrolling when touching the ISI header
        this._infoHeader.addEventListener('touchmove', (e) => e.preventDefault());

        // handleBar click-to-expand
        this._popupHandleBar?.addEventListener('click', () => this._handleBarClick());

        // indications headline click
        this._indicationsHeader.addEventListener('click', () => this._toggleIndicationsInformation());

        // safety information headline click
        this._infoHeader.addEventListener('click', () => this._toggleSafetyInformation());

        // Manual close
        this._popup
            .querySelector('.js_popupCloseBtn')
            .addEventListener('click', () => this.dismiss(DISMISS_TYPE.MANUAL_DISMISS));

        // Expanded scroll
        this._handleScroll(this._popupInnerWrap, 'expanded_scroll');

        // URL click
        Array.from(this._popup.querySelectorAll('a')).forEach((linkItem) => {
            linkItem.addEventListener('click', () => {
                const data = { text: linkItem.textContent, url: linkItem.href };
                this.trackEvent({ eventName: 'url_click', data });
                return false;
            });
        });

        // Expanded headline click
        this._popupHeader.addEventListener('click', () => {
            if (this._popupState !== STATE.CLOSED) {
                const data = getScrollPosition(this._popupInnerWrap);
                this.trackEvent({ eventName: 'expanded_headline_click', data });
                this._resetShowNextTimeout();
            }
        });

        // Expanded body click
        this._popupContent.addEventListener('click', () => {
            if (this._popupState !== STATE.CLOSED) {
                const data = getScrollPosition(this._popupInnerWrap);
                this.trackEvent({ eventName: 'expanded_click', data });
                this._resetShowNextTimeout();
            }
        });

        // Indications click
        this._indications.addEventListener('click', () => {
            const data = getScrollPosition(this._indications);
            this.trackEvent({ eventName: 'indications_click', data });
            this._resetShowNextTimeout();
        });

        // Indications scroll
        this._handleScroll(this._indicationsContent, 'indications_scroll');

        // ISI scroll
        this._handleScroll(this._popupInfoContent, 'isi_scroll');

        // ISI click
        this._info.addEventListener('click', () => {
            const data = getScrollPosition(this._info);
            this.trackEvent({ eventName: 'isi_click', data });
            this._resetShowNextTimeout();
        });
    }

    trackEvent(eventData) {
        const headers = this.client.getAjaxHeaders();
        const data = JSON.stringify({
            brandAlertViewedId: this._brandAlertViewedId,
            fsSessionId: this.client.getFsSessionId(),
            ...eventData,
        });

        $.ajax({
            url: context.brandAlertTrackingUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            method: 'POST',
            data,
            headers,
        });
    }

    expand() {
        this.client.expand();

        this._popupContainer.classList.add('is-half-expanded');
        this._popupState = STATE.HALF_EXPANDED;
        this._popupContainer.style.transform = 'translateY(0)';

        this._shownTime = Date.now();

        this.trackEvent({ eventName: 'expanded_shown' });
        this._resetShowNextTimeout();
        this._popup.dataset.headerRows = this._popupHeaderLinks.clientHeight / 18;
    }

    getExpandedDurationData() {
        return this._shownTime ? { expandedDuration: Date.now() - this._shownTime } : {};
    }

    finalizeAlert(dismissType, dismissData) {
        let data = this.getExpandedDurationData();
        if (dismissData) {
            data = { ...dismissData, ...data };
        }

        this._hoverTrackers.forEach((x) => x.finalize());
        this.trackEvent({ eventName: dismissType, data });
    }

    dismiss(dismissType, data) {
        if (this._popupState === STATE.CLOSED) {
            return;
        }

        if (dismissType === DISMISS_TYPE.MANUAL_DISMISS) {
            clearTimeout(this._showNextTimeout);
        }

        this.finalizeAlert(dismissType, data);
        this.client.closePopup();
        this.client._showComments();
    }

    _toggleSafetyInformation() {
        this._secondaryWrap.classList.toggle('safety-expanded');
        this._secondaryWrap.classList.remove('indications-expanded');
    }

    _toggleIndicationsInformation() {
        this._secondaryWrap.classList.toggle('indications-expanded');
        this._secondaryWrap.classList.remove('safety-expanded');
    }

    resize() {
        this._popupContainer.classList.add('transition-disabled');
        this._popupContainer.style.transform = 'translateY(0)';

        setTimeout(() => {
            this._popupContainer.classList.remove('transition-disabled');
        }, 500);
    }

    _handleBarClick() {
        const windowHeight = window.innerHeight;
        const video = document.querySelector('.js_videoTab video');
        const videoHeight = video.getBoundingClientRect().height;

        if (this._popupState === STATE.FULL_EXPANDED) {
            this._popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
            this._popupContainer.classList.remove('is-full-expanded');
            this._popupState = STATE.HALF_EXPANDED;
            this.trackEvent({ eventName: 'handlebar_collapsed' });
        } else {
            this._popupContainer.classList.add('is-full-expanded');
            this._popupState = STATE.FULL_EXPANDED;
            this._popupContainer.style.height = `${windowHeight}px`;
            this.trackEvent({ eventName: 'handlebar_expanded' });
        }
    }

    _resetShowNextTimeout() {
        if (this._showNextTimeout) {
            clearTimeout(this._showNextTimeout);
        }

        this._showNextTimeout = setTimeout(() => this.client._showNext(), this._dismissTimeSeconds * 1000);
    }

    _handleScroll(element, eventName) {
        // eslint-disable-next-line
        // Source: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
        const checkScrollToBottom = () =>
            Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) <= 1;

        let scrolledToBottom = false;
        element.addEventListener(
            'scroll',
            debounce(() => {
                let trackingEventName = eventName;
                if (!scrolledToBottom && checkScrollToBottom()) {
                    trackingEventName += '_bottom';
                    scrolledToBottom = true;
                }

                this.trackEvent({ eventName: trackingEventName, data: getScrollPosition(element) });
                this._resetShowNextTimeout();
            }, 300),
        );
    }
}

class BrandAlertsClient {
    constructor() {
        this._playStartedTime = null;
        this._expandTimeSeconds = null;

        this._popupTemplate = null;
        this.activeBrandAlert = null;
        this.nextBrandAlert = null;

        this._popupFrame = document.querySelector('.js_popupFrame');
        // TODO: change this? For channel pages where there's no alerts HTML
        if (!this._popupFrame) {
            // eslint-disable-next-line no-useless-return
            return;
        }
    }

    start() {
        this._watchPlaybackStart();
        this._loadAd().done((data) => {
            const popup = this._popupFrame.querySelector('.js_brandAlertPopup');
            this.activeBrandAlert = new BrandAlertPopup(popup, data, this, 1);
            this._expandTimeSeconds = data.expand_time_seconds;
            this._setPopupHeight();
            this._popupFrame.classList.remove('is-hidden');
            this._checkPlayStarted();
            this._handleEvents();
        });
    }

    _loadAd() {
        const data = {
            viewed_uuid: context.viewedUUID,
            fsSessionId: this.getFsSessionId(),
            ...this._getLoadAjaxData(),
        };
        const headers = this.getAjaxHeaders();

        return $.ajax({
            url: context.brandAlertCreativeUrl,
            headers,
            data,
        });
    }

    _checkPlayStarted() {
        if (!this._playStartedTime) {
            setTimeout(() => this._checkPlayStarted(), 1000);
        } else {
            const remainingTimeout = Math.max(this._expandTimeSeconds * 1000 - (Date.now() - this._playStartedTime), 0);
            setTimeout(() => this._showExpanded(), remainingTimeout);
        }
    }

    _showExpanded() {
        if (this._commentsVisible()) {
            this.activeBrandAlert.trackEvent({ eventName: 'comments_prevent_showing' });
            return;
        }

        if (this._isFullscreen() || this._tooSmallScreenHeight() || this._interstitialVisible()) {
            // TODO Track this event
            setTimeout(() => this._showExpanded(), 1000);
        } else {
            this._hideComments();
            this.activeBrandAlert.expand();
            this._popupTemplate = this.activeBrandAlert._popup.cloneNode(true);
        }
    }

    _handleEvents() {
        this._commentEvents();
        this._fullscreenEvents();
        this._unloadEvents();
        this._resizeEvents();
        this._focusInEvents();
        this._focusOutEvents();
    }

    _showNext() {
        if (this.activeBrandAlert._popupState == STATE.CLOSED) {
            return;
        }
        const headers = this.getAjaxHeaders();
        return $.ajax({
            url: context.brandAlertCreativeUrl,
            headers,
            data: { viewed_uuid: context.viewedUUID, fsSessionId: this.getFsSessionId() },
        })
            .done((data) => {
                const popup = this._popupFrame.querySelector('.js_brandAlertPopup');
                const nextPopup = this._popupTemplate.cloneNode(true);
                nextPopup.classList.add('slide-right');
                popup.after(nextPopup);
                const { popupNum } = this.activeBrandAlert;
                this.nextBrandAlert = new BrandAlertPopup(nextPopup, data, this, popupNum + 1);
                this.activeBrandAlert.finalizeAlert(DISMISS_TYPE.AUTO_DISMISS);
                setTimeout(() => {
                    this._makeTransition();
                }, 100);
            })
            .fail(() => {
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS);
            });
    }

    _makeTransition() {
        this.activeBrandAlert._popup.classList.add('slide-left');
        this.nextBrandAlert._popup.classList.remove('slide-right');

        setTimeout(() => {
            this.activeBrandAlert._popup.remove();
            this.activeBrandAlert = this.nextBrandAlert;
            this.nextBrandAlert = null;
            this.activeBrandAlert._popupState = STATE.HALF_EXPANDED;
            this.activeBrandAlert.trackEvent({ eventName: 'expanded_shown' });
            this.activeBrandAlert._resetShowNextTimeout();
            this.activeBrandAlert._shownTime = Date.now();
        }, 500);
    }
}

export class WebBrandAlertsClient extends BrandAlertsClient {
    constructor(player) {
        super();
        this._player = player;
        this._commentsWrapper = document.querySelector('.js_commentsWrapper');
        this._showAllCommentsButton = document.querySelector('.js_commentsShowAllButton');
        this._commentBox = document.querySelector('.js_commentBox');
    }

    _watchPlaybackStart() {
        const setPlayStartedTime = () => (this._playStartedTime = this._playStartedTime || Date.now());
        this._player.one('play', setPlayStartedTime);
        this._player.one('contentresumed', setPlayStartedTime);
    }

    _setPopupHeight() {
        if (window.innerWidth >= 992) {
            const relatedContentOffsetTop = document.querySelector('.related-sidebar').offsetTop;
            const result = window.innerHeight - relatedContentOffsetTop;
            this._popupFrame.style.height = `${result}px`;
        }
    }

    _getLoadAjaxData() {
        const searchParams = new URLSearchParams(window.location.search);
        const forceBrandAlertCreativeId = searchParams.get('force_brand_alert_creative_id');
        if (forceBrandAlertCreativeId) {
            return { force_brand_alert_creative_id: forceBrandAlertCreativeId };
        }

        return {};
    }

    getAjaxHeaders() {
        return { 'X-CSRFToken': window.csrftoken() || context.csrftoken, Accept: 'application/json' };
    }

    expand() {
        const video = document.querySelector('.js_videoTab video');
        const videoHeight = video.getBoundingClientRect().height;
        const videoTopOffset = video.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const portraitOrientation = windowWidth < windowHeight;

        // on mobile
        // if the video is bellow the top of the viewport (not scrolled over)
        // scroll the page so that the top of the video aligns with the top of the viewport
        // and align the top of the alert with the bottom of the video
        // Double scroll because of potential Apple smart banner on iOS Safari w/ app installed
        if (portraitOrientation && windowWidth < 992) {
            if (videoTopOffset > 0) {
                setTimeout(() => video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' }), 100);
                video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
            }
            this.activeBrandAlert._popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
        }
    }

    _commentsVisible() {
        // If the comments section is not present or empty, say it's not visible
        if (!this._commentsWrapper || !this._commentsWrapper.querySelector('ul > *')) {
            return false;
        }

        // If present, check if it's in viewport
        const { top, left, bottom, right } = this._commentsWrapper.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;

        const commentsInViewport =
            ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
            ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth));

        // Visible if writing comments, or comments in viewport
        return document.activeElement === this._commentBox || commentsInViewport;
    }

    _isFullscreen() {
        return this._player.isFullscreen();
    }

    _tooSmallScreenHeight() {
        return window.innerHeight < 390;
    }

    _interstitialVisible() {
        return !!document.querySelector('#subscriptions-form-modal.show');
    }

    _hideComments() {
        this._commentsWrapper?.classList.add('is-collapsed');
        this._showAllCommentsButton?.classList.remove('hidden');
        this._showAllCommentsButton?.classList.add('d-flex');
    }

    _showComments() {
        if (!this._commentsWrapper) {
            return;
        }

        this._commentsWrapper.classList.remove('is-collapsed');
        this._showAllCommentsButton.classList.remove('d-flex');
        this._showAllCommentsButton.classList.add('hidden');
        if (
            this.activeBrandAlert &&
            (this.activeBrandAlert._popupState !== STATE.START || this.activeBrandAlert._popupState !== STATE.CLOSED)
        ) {
            this.activeBrandAlert.dismiss(DISMISS_TYPE.COMMENTS_ACTIVE_DISMISS);
        }
    }

    _commentEvents() {
        // Dismiss brand alert if "Show All Comments" button is clicked
        this._showAllCommentsButton?.addEventListener('click', () => this._showComments());

        // Dismiss brand alert if comments box is focused
        this._commentBox?.addEventListener('focus', () => this._showComments());
    }

    _fullscreenEvents() {
        // Dismiss if enter full screen
        this._player.on('fullscreenchange', () => {
            if (this._player.isFullscreen() && this.activeBrandAlert._shownTime) {
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: 'full screen' });
            }
        });
    }

    _unloadEvents() {
        // Page close
        window.addEventListener('beforeunload', () => {
            this.activeBrandAlert.dismiss(DISMISS_TYPE.PAGE_CLOSE_DISMISS);
        });
    }

    _resizeEvents() {
        // Responsive resizing
        window.addEventListener('resize', () => {
            // recalculate the number of header link rows to apply correct styles for expanded view
            const popupHeaderLinks = document.querySelector('.js_headerLinks');
            if (this.activeBrandAlert._popup) {
                this.activeBrandAlert._popup.dataset.headerRows = popupHeaderLinks.clientHeight / 18;
            }

            if (this.activeBrandAlert && this.activeBrandAlert._popupState === STATE.START) return;

            if (window.innerHeight < 390) {
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: 'height < 390px' });
            } else {
                this._setPopupHeight();
            }
        });
    }

    _focusOutEvents() {
        window.addEventListener('blur', () => {
            const data = this.activeBrandAlert.getExpandedDurationData();
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_lost', data });
        });
    }

    _focusInEvents() {
        window.addEventListener('focus', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_retrieved' });
        });
    }

    closePopup() {
        this.activeBrandAlert._popupContainer.classList.add('is-closed');
        this.activeBrandAlert._popupState = STATE.CLOSED;
        this.activeBrandAlert._popup.removeAttribute('style');
    }

    getFsSessionId() {
        return window.FS?.getCurrentSession?.();
    }
}

export class IosBrandAlertsClient extends BrandAlertsClient {
    constructor(player) {
        super();
        this._commentsVisibleFlag = false;
        this._fullscreenActive = false;
        this._screenTooSmall = false;
        this._interstitialVisibleFlag = false;
    }

    _watchPlaybackStart() {
        // iOS:
        //      On playback start: window.dispatchEvent(new Event('ios_playback_started'));
        window.addEventListener('ios_playback_started', () => (this._playStartedTime = Date.now()));
    }

    _setPopupHeight() {
        const doubleISIPopupSecondaryWrap = document.querySelector('.brand_alert_popup--double_isi .js_secondaryWrap');
        this.activeBrandAlert._popup.style.top = '24px';
        this._popupFrame.style.height = '100vh';

        if (doubleISIPopupSecondaryWrap) {
            doubleISIPopupSecondaryWrap.style.top = '20px';
        }
    }

    _getLoadAjaxData() {
        // TODO: Still need to get this into context on iOS somehow
        if (context.forceBrandAlertCreativeId) {
            return { force_brand_alert_creative_id: context.forceBrandAlertCreativeId };
        }
    }

    getAjaxHeaders() {
        return { Authorization: context.accessToken, Accept: 'application/json' };
    }

    expand() {
        // iOS: expand the popup to fist snap point
        this.activeBrandAlert._popupContainer.style.height = '100vh';
        window.location = 'expand';

        setTimeout(() => {
            // disable transitions for smoother expanding on ios
            this.activeBrandAlert._popupContainer.classList.add('transitions-disabled');
        }, 500);
    }

    _commentsVisible() {
        return this._commentsVisibleFlag;
    }

    _isFullscreen() {
        return this._fullscreenActive;
    }

    _tooSmallScreenHeight() {
        return this._screenTooSmall;
    }

    _interstitialVisible() {
        return this._interstitialVisibleFlag;
    }

    _hideComments() {
        // Cannot happen on iPhone
    }

    _showComments() {
        // Cannot happen on iPhone
    }

    _handleEvents() {
        super._handleEvents();
        this._interstitialEvents();
        this._handleBarClick();
    }

    _interstitialEvents() {
        // iOS:
        //      On interstitial window visible: window.dispatchEvent(new Event('ios_interstitial_visible'));
        //      On interstitial window invisible: window.dispatchEvent(new Event('ios_interstitial_invisible'));
        window.addEventListener('ios_interstitial_visible', () => (this._interstitialVisibleFlag = true));
        window.addEventListener('ios_interstitial_invisible', () => (this._interstitialVisibleFlag = false));
    }

    _commentEvents() {
        // iOS:
        //      On comment window active: window.dispatchEvent(new Event('ios_comments_visible'));
        //      On comment window inactive: window.dispatchEvent(new Event('ios_comments_invisible'));
        window.addEventListener('ios_comments_visible', () => (this._commentsVisibleFlag = true));
        window.addEventListener('ios_comments_invisible', () => (this._commentsVisibleFlag = false));
    }

    _fullscreenEvents() {
        // iOS:
        //      On fullscreen enter: window.dispatchEvent(new Event('ios_fullscreen_enter'));
        //      On fullscreen exit: window.dispatchEvent(new Event('ios_fullscreen_exit'));
        window.addEventListener('ios_fullscreen_enter', () => {
            this._fullscreenActive = true;
            if (this.activeBrandAlert._shownTime) {
                this.activeBrandAlert.dismiss('auto_dismiss', { reason: 'full screen' });
            }
        });

        window.addEventListener('ios_fullscreen_exit', () => (this._fullscreenActive = false));
    }

    _unloadEvents() {
        // iOS:
        //      On leaving page: window.dispatchEvent(new Event('ios_unload'));
        window.addEventListener('ios_unload', () => this.activeBrandAlert.dismiss(DISMISS_TYPE.PAGE_CLOSE_DISMISS));
    }

    _resizeEvents() {
        // iOS:
        //      On video screen load if the screen is <390px high:
        //      window.dispatchEvent(new Event('ios_screen_too_small'));
        window.addEventListener('ios_screen_too_small', () => (this._screenTooSmall = true));
    }

    _focusOutEvents() {
        window.addEventListener('ios_focus_lost', () => {
            const data = this.activeBrandAlert.getExpandedDurationData();
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_lost', data });
        });
    }

    _focusInEvents() {
        window.addEventListener('ios_focus_retrieved', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_retrieved' });
        });
    }

    _handleBarClick() {
        window.addEventListener('ios_handlebar_expanded', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'handlebar_expanded' });
        });

        window.addEventListener('ios_handlebar_collapsed', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'handlebar_collapsed' });
        });
    }

    closePopup() {
        // iOS: should collapse the popup window
        window.location = 'close';
    }

    getFsSessionId() {}
}
